import { useMemo, useEffect } from 'react';
import ReactFlow, {
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Node,
  type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { OperatorNode } from './nodes/OperatorNode';
import { NODE_META } from './nodeTypes';

const NODE_TYPES = Object.fromEntries(
  Object.keys(NODE_META).map((k) => [k, OperatorNode])
) as Record<string, typeof OperatorNode>;

const EDGE_DEFAULTS = {
  style: { stroke: '#a1a1aa', strokeWidth: 2 },
  type: 'smoothstep' as const,
  markerEnd: { type: MarkerType.ArrowClosed, color: '#a1a1aa', width: 14, height: 14 },
};

interface NodeDSL {
  id: string;
  type: string;
  position?: { x: number; y: number };
  label?: string;
  config?: Record<string, unknown>;
}

interface EdgeDSL {
  from: string;
  to: string;
}

export interface WorkflowReadOnlyCanvasProps {
  nodes: NodeDSL[];
  edges: EdgeDSL[];
  className?: string;
}

function dslToFlowNodes(nodes: NodeDSL[]): Node[] {
  return nodes.map((n) => {
    const type = n.type ?? 'filter';
    const pos = n.position ?? { x: 0, y: 0 };
    const x = typeof pos.x === 'number' ? pos.x : 0;
    const y = typeof pos.y === 'number' ? pos.y : 0;
    const config = (n.config?.params ? { type, params: n.config.params } : n.config) ?? { type, params: {} };
    const label = n.label ?? NODE_META[type]?.label ?? type;
    
    return {
      id: n.id,
      type: type in NODE_TYPES ? type : 'filter',
      position: { x, y },
      data: {
        label,
        config,
        status: 'idle' as const,
        readOnly: true,
      },
      selected: false,
    };
  });
}

function dslToFlowEdges(edges: EdgeDSL[]): Edge[] {
  return edges.map((e, i) => ({
    id: `e_${e.from}_${e.to}_${i}`,
    source: e.from,
    target: e.to,
    ...EDGE_DEFAULTS,
  }));
}

export function WorkflowReadOnlyCanvas({
  nodes,
  edges,
  className = '',
}: WorkflowReadOnlyCanvasProps) {
  const initialNodes = useMemo(() => dslToFlowNodes(nodes), [nodes]);
  const initialEdges = useMemo(() => dslToFlowEdges(edges), [edges]);

  const [rfNodes, setRfNodes] = useNodesState(initialNodes);
  const [rfEdges, setRfEdges] = useEdgesState(initialEdges);

  useEffect(() => {
    setRfNodes(dslToFlowNodes(nodes));
  }, [nodes, setRfNodes]);

  useEffect(() => {
    setRfEdges(dslToFlowEdges(edges));
  }, [edges, setRfEdges]);

  return (
    <div className={`flex-1 h-full min-w-0 ${className}`}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.35, maxZoom: 1.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        selectNodesOnDrag={false}
        proOptions={{ hideAttribution: true }}
        className="bg-zinc-50"
        defaultEdgeOptions={EDGE_DEFAULTS}
      >
        <Controls
          className="!shadow-none !border !border-zinc-200 !rounded-xl overflow-hidden"
          showFitView
          showInteractive={false}
        />
      </ReactFlow>
    </div>
  );
}
